import { db } from '../db/dexie';
import { TRASH_RETENTION_DAYS } from '../db/types';
import { purgeBlob } from './blobs';
import { currentUserId, supabase } from './supabase';

const DAY_MS = 86_400_000;

/**
 * Destroys an item for good: its row, its attachment rows, their binaries in
 * Storage, and the cached copies of those binaries on this device.
 *
 * This is the only code in the application that hard-deletes anything. Every
 * other delete is a tombstone, because a tombstone is how the other device
 * learns something is gone — and a purge deliberately gives that up. The other
 * device keeps its own tombstone until its own sweep reaches it, which is
 * harmless: the item is already invisible on both, sitting in a Trash it can
 * never leave.
 *
 * The server goes first. Purging locally while the delete is still owed would
 * leave a live row on the server that the next pull hands straight back.
 */
export async function purgeItem(itemId: string): Promise<void> {
  const attachments = await db.attachments.where('item_id').equals(itemId).toArray();

  if (supabase && (await currentUserId())) {
    // `attachments.item_id` cascades, so the item row takes its metadata with
    // it. The bytes in Storage are not part of that cascade and are removed
    // explicitly below.
    const { error } = await supabase.from('items').delete().eq('id', itemId);
    if (error) throw error;
  }

  for (const attachment of attachments) {
    await purgeBlob(attachment.id, attachment.storage_path);
  }

  await db.transaction('rw', db.items, db.attachments, async () => {
    await db.attachments.bulkDelete(attachments.map((a) => a.id));
    await db.items.delete(itemId);
  });
}

/**
 * Purges everything whose retention has run out. Called once on start; the
 * window is measured in days, so there is nothing to gain from checking more
 * often than the app is opened.
 *
 * A failure on one item must not strand the rest — the most likely cause is a
 * single missing Storage object, which is not a reason to keep the other
 * thirty. Returns how many were actually destroyed.
 */
export async function sweepTrash(retentionDays = TRASH_RETENTION_DAYS): Promise<number> {
  // StrictMode mounts effects twice in development. The purge is idempotent, so
  // a second pass is harmless, but it would spend a second round trip per item
  // discovering the server already agrees.
  inFlight ??= runSweep(retentionDays).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

let inFlight: Promise<number> | null = null;

async function runSweep(retentionDays: number): Promise<number> {
  const cutoff = new Date(Date.now() - retentionDays * DAY_MS).toISOString();

  const expired = await db.items
    .where('deleted_at')
    .below(cutoff)
    .filter((item) => item.deleted)
    .toArray();

  let purged = 0;
  for (const item of expired) {
    try {
      await purgeItem(item.id);
      purged += 1;
    } catch (cause) {
      console.warn(`[trash] could not purge ${item.id}`, cause);
    }
  }
  return purged;
}
